import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, MAGAZZINI, STAGIONI, MARCHI_PNEUMATICO } from "@/lib/api";
import { Plus, Search, Package, Edit, Trash2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";

export default function Magazzino() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMarca, setFilterMarca] = useState("");
  const [filterStag, setFilterStag] = useState("");
  const [filterMag, setFilterMag] = useState("");
  const [includeEsauriti, setIncludeEsauriti] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_esauriti: includeEsauriti };
      if (search) params.search = search;
      if (filterMarca) params.marca = filterMarca;
      if (filterStag) params.stagionalita = filterStag;
      if (filterMag) params.magazzino = filterMag;
      const { data } = await api.get("/inventory", { params });
      setItems(data);
      const s = await api.get("/inventory/stats");
      setStats(s.data);
    } catch (err) {
      toast.error("Errore caricamento magazzino");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterMarca, filterStag, filterMag, includeEsauriti]);

  const doScarico = async (item) => {
    const raw = window.prompt(
      `SCARICO — ${item.marca} ${item.misura}\nDisponibili: ${item.quantita}\nQuante gomme scarichi (vendute)?`,
      "1"
    );
    if (!raw) return;
    const q = parseInt(raw, 10);
    if (!q || q <= 0) return toast.error("Quantita' non valida");
    if (q > item.quantita) return toast.error(`Massimo scaricabili: ${item.quantita}`);
    try {
      await api.post(`/inventory/${item.id}/scarico`, { quantita: q });
      toast.success(`Scaricate ${q} gomme`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore scarico");
    }
  };

  const doCarico = async (item) => {
    const raw = window.prompt(
      `CARICO — ${item.marca} ${item.misura}\nDisponibili ora: ${item.quantita}\nQuante gomme aggiungi (nuovo arrivo)?`,
      "4"
    );
    if (!raw) return;
    const q = parseInt(raw, 10);
    if (!q || q <= 0) return toast.error("Quantita' non valida");
    try {
      await api.post(`/inventory/${item.id}/carico`, { quantita: q });
      toast.success(`Caricate ${q} gomme`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore carico");
    }
  };

  const doDelete = async (item) => {
    if (!window.confirm(`Eliminare definitivamente ${item.marca} ${item.misura}?`)) return;
    try {
      await api.delete(`/inventory/${item.id}`);
      toast.success("Articolo eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const marchiUnici = useMemo(() => {
    const set = new Set(items.map((i) => i.marca).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="space-y-6" data-testid="magazzino-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">Stock nuove gomme</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
            Magazzino Pneumatici
          </h1>
        </div>
        <button
          onClick={() => navigate("/magazzino/nuovo")}
          data-testid="magazzino-new-button"
          className="bg-[#E60000] text-white border-2 border-black px-5 py-3 font-bold uppercase tracking-widest hover:bg-black inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Carica gomme
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="magazzino-stats">
          <StatCard label="Articoli disponibili" value={stats.total_items_disponibili} />
          <StatCard label="Totale gomme" value={stats.totale_gomme} />
          <StatCard label="Estive" value={stats.per_stagionalita?.Estivo || 0} />
          <StatCard label="Invernali" value={stats.per_stagionalita?.Invernale || 0} />
        </div>
      )}

      {/* Filters */}
      <section className="border-2 border-black bg-white p-4 md:p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/50" />
            <input
              data-testid="magazzino-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca marca, misura, indice..."
              className="w-full border-2 border-black pl-10 pr-3 py-3 outline-none focus:border-[#E60000] font-bold"
            />
          </div>
          <select
            data-testid="magazzino-filter-stag"
            value={filterStag}
            onChange={(e) => setFilterStag(e.target.value)}
            className="border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
          >
            <option value="">Tutte le stagioni</option>
            {STAGIONI.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            data-testid="magazzino-filter-mag"
            value={filterMag}
            onChange={(e) => setFilterMag(e.target.value)}
            className="border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
          >
            <option value="">Tutti i magazzini</option>
            {MAGAZZINI.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="magazzino-filter-marca"
            value={filterMarca}
            onChange={(e) => setFilterMarca(e.target.value)}
            className="border-2 border-black p-2 outline-none focus:border-[#E60000] bg-white font-bold text-sm"
          >
            <option value="">Tutte le marche</option>
            {MARCHI_PNEUMATICO.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <label className="flex items-center gap-2 border-2 border-black px-3 py-2 cursor-pointer hover:bg-black hover:text-white transition-colors">
            <input
              type="checkbox"
              data-testid="magazzino-toggle-esauriti"
              checked={includeEsauriti}
              onChange={(e) => setIncludeEsauriti(e.target.checked)}
              className="w-4 h-4 accent-[#E60000]"
            />
            <span className="font-bold uppercase text-xs tracking-widest">Mostra esauriti</span>
          </label>
          {(search || filterMarca || filterStag || filterMag) && (
            <button
              onClick={() => { setSearch(""); setFilterMarca(""); setFilterStag(""); setFilterMag(""); }}
              className="text-xs font-bold uppercase tracking-widest text-[#E60000] hover:underline"
            >
              Azzera filtri
            </button>
          )}
        </div>
      </section>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 font-bold uppercase tracking-widest text-black/50">Caricamento...</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-black p-12 text-center">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-40" strokeWidth={2} />
          <div className="font-bold uppercase tracking-widest">Nessun articolo</div>
          <div className="text-xs mt-2 text-black/60">
            {includeEsauriti ? "Prova a modificare i filtri" : "Clicca 'Carica gomme' per aggiungere un nuovo articolo"}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="magazzino-list">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`magazzino-card-${it.id}`}
              className={`border-2 border-black p-4 bg-white flex flex-col gap-3 ${it.quantita === 0 ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-black text-xl uppercase tracking-tight">{it.marca}</div>
                  {it.modello && <div className="text-xs font-bold uppercase tracking-widest text-black/70">{it.modello}</div>}
                </div>
                <div className={`px-3 py-1 border-2 border-black font-mono-data font-black text-lg ${it.quantita === 0 ? "bg-black text-white" : "bg-[#E60000] text-white"}`}>
                  {it.quantita} pz
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-black/50">Misura</div>
                  <div className="font-mono-data font-bold">{it.misura}</div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-black/50">Indici</div>
                  <div className="font-mono-data font-bold">
                    {[it.indice_carico, it.indice_velocita].filter(Boolean).join(" ") || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-black/50">Stagione</div>
                  <div className="font-bold">{it.stagionalita || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-black/50">Posizione</div>
                  <div className="font-bold">{it.magazzino} · S{it.settore} · {it.scaffale}</div>
                </div>
              </div>
              {it.note && <div className="text-xs text-black/70 border-t border-black/20 pt-2">{it.note}</div>}
              <div className="text-[10px] font-mono-data text-black/50 border-t border-black/10 pt-2">
                Caricate totale: {it.quantita_iniziale || 0} · Vendute: {it.quantita_venduta || 0}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={() => doScarico(it)}
                  disabled={it.quantita === 0}
                  data-testid={`scarico-${it.id}`}
                  className="border-2 border-black bg-white text-black hover:bg-[#E60000] hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-black font-bold uppercase text-xs tracking-widest py-2 inline-flex items-center justify-center gap-1"
                >
                  <ArrowDownCircle className="w-4 h-4" strokeWidth={3} /> Scarica
                </button>
                <button
                  onClick={() => doCarico(it)}
                  data-testid={`carico-${it.id}`}
                  className="border-2 border-black bg-black text-white hover:bg-[#E60000] font-bold uppercase text-xs tracking-widest py-2 inline-flex items-center justify-center gap-1"
                >
                  <ArrowUpCircle className="w-4 h-4" strokeWidth={3} /> Carica
                </button>
              </div>
              <div className="flex justify-end gap-2 text-[10px]">
                <button
                  onClick={() => navigate(`/magazzino/${it.id}`)}
                  data-testid={`edit-${it.id}`}
                  className="inline-flex items-center gap-1 font-bold uppercase tracking-widest hover:text-[#E60000]"
                >
                  <Edit className="w-3 h-3" strokeWidth={3} /> Modifica
                </button>
                <button
                  onClick={() => doDelete(it)}
                  data-testid={`delete-${it.id}`}
                  className="inline-flex items-center gap-1 font-bold uppercase tracking-widest hover:text-[#E60000]"
                >
                  <Trash2 className="w-3 h-3" strokeWidth={3} /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div className="border-2 border-black bg-white p-4">
    <div className="text-[10px] font-black uppercase tracking-widest text-black/60">{label}</div>
    <div className="font-display font-black text-3xl mt-1">{value}</div>
  </div>
);
